// Sync history panel (BUILD_SPEC.md §5.9, §12; goal G5) — services/api/src/modules/
// xero/sync-log.ts's `GET /xero/sync-log`. `xero_sync_log` already recorded every
// push/pull attempt (push.ts/pull.ts/retry.ts, goal G17) but had no UI to see it;
// this is a read-only register with a status filter and cursor "Load more", same
// role gate as XeroSyncActions (SALES_ACCOUNTS/ADMIN — the surface that produces
// these rows in the first place).
import { useState } from 'react';
import { Alert, Badge, Button, Group, Paper, Select, Stack, Table, Text, Title, Tooltip } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { XERO_SYNC_STATUSES, type XeroSyncStatus } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { DocStatusBadge } from '../../../components/DocStatusBadge';
import { useCanSyncXero } from '../permissions';
import { useXeroSyncLog } from '../api';

const STATUS_OPTIONS = XERO_SYNC_STATUSES.map((s) => ({ value: s, label: s }));
const ERROR_TRUNCATE_LEN = 60;

function ErrorCell({ error }: { error: string | null }) {
  if (!error) {
    return (
      <Text size="xs" c="dimmed">
        —
      </Text>
    );
  }
  const isTruncated = error.length > ERROR_TRUNCATE_LEN;
  const shown = isTruncated ? `${error.slice(0, ERROR_TRUNCATE_LEN)}…` : error;
  return (
    <Tooltip label={error} multiline w={340} withArrow disabled={!isTruncated}>
      <Text size="xs" c="red" style={isTruncated ? { cursor: 'help' } : undefined}>
        {shown}
      </Text>
    </Tooltip>
  );
}

export function XeroSyncHistory() {
  const canSync = useCanSyncXero();
  const [status, setStatus] = useState<XeroSyncStatus | null>(null);
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const query = useXeroSyncLog({ status: status ?? undefined, cursor });
  const rows = query.data?.items ?? [];

  if (!canSync) {
    return (
      <Paper withBorder radius="md" p="lg">
        <Text size="sm" c="dimmed">
          SALES_ACCOUNTS or ADMIN role is required to view Xero sync history.
        </Text>
      </Paper>
    );
  }

  function handleStatusChange(value: string | null) {
    setStatus((value as XeroSyncStatus | null) ?? null);
    setCursor(undefined);
  }

  return (
    <Paper withBorder radius="md" p="lg">
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={3}>Sync history</Title>
          <Select
            placeholder="All statuses"
            clearable
            data={STATUS_OPTIONS}
            value={status}
            onChange={handleStatusChange}
            w={160}
            size="xs"
            comboboxProps={{ withinPortal: true }}
          />
        </Group>

        {query.isError && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {query.error instanceof ApiRequestError ? query.error.message : 'Failed to load Xero sync history.'}
          </Alert>
        )}

        <Table.ScrollContainer minWidth={640}>
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Created</Table.Th>
                <Table.Th>Direction</Table.Th>
                <Table.Th>Entity</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Error</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>{new Date(row.createdAt).toLocaleString()}</Table.Td>
                  <Table.Td>
                    <Badge size="xs" variant="light">
                      {row.direction}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {row.entityType} {row.entityId.slice(-6)}
                  </Table.Td>
                  <Table.Td>
                    <DocStatusBadge status={row.status} domain="xeroSync" />
                  </Table.Td>
                  <Table.Td>
                    <ErrorCell error={row.error} />
                  </Table.Td>
                </Table.Tr>
              ))}
              {!query.isLoading && rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text c="dimmed" ta="center" py="md">
                      No Xero sync activity yet.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>

        {query.data?.nextCursor && (
          <Group justify="center">
            <Button variant="default" size="xs" onClick={() => setCursor(query.data?.nextCursor ?? undefined)}>
              Load more
            </Button>
          </Group>
        )}
      </Stack>
    </Paper>
  );
}
